/* eslint react-hooks/immutability: ["warn"] */
import React, { useMemo } from "react";
import { useTranslation } from "@coworker/locales";
import { CriteriaList } from "./CriterionComponent/CriteriaList";
import { updateCriterionReducer } from "./CriterionComponent/updateCriterionReducer";
import {
  allCriteriaAnswered,
  allCriteriaOk,
} from "../CommonComponents/article.helpers";
import { useTestbuy } from "../CommonComponents/Repositories/useTestbuyService";
import { isDateWithinLastWeek } from "../CommonComponents/helpers";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { ModalContext } from "@coworker/apprestructured/src/layout/context/ModalContextProvider";
import { ModalType } from "@coworker/apprestructured/src/layout/context/modalType";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import { useNavigate } from "react-router";
import useFormatter from "@coworker/app/src/hooks/useFormatter";
import { TaskType } from "@coworker/apprestructured/src/tasks/enums/taskTypes";
import { TestbuyItemCard } from "@coworker/apprestructured/src/testbuy/components/TestbuyItemCard/TestbuyItemCard";
import { useItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";
import SimpleArticleCardSkeleton from "@coworker/apprestructured/src/shared/simple/SimpleSkeleton/SimpleArticleCardSkeleton";
import { TestBuyProductWithCriteria } from "@coworker/types/lib/testbuy";
import styles from "./display-article-details.module.css";
import { useGetTestbuyFollowUpTask } from "@coworker/apprestructured/src/testbuy/hooks/useGetTestbuyFollowUpTask";

interface DisplayArticleDetailsProps {
  testbuyItem: TestBuyProductWithCriteria;
  testbuyId: string;
}

export function DisplayArticleDetails({
  testbuyId,
  testbuyItem,
}: DisplayArticleDetailsProps) {
  const { t } = useTranslation();
  const navigate = useFixaNavigate();
  const { formatDateISO8601 } = useFormatter();

  const { toggleModal } = React.useContext(ModalContext);
  const [criteriaState, dispatch] = React.useReducer(
    updateCriterionReducer,
    testbuyItem?.criteria
  );
  const [loadButton, setLoadButton] = React.useState<boolean>(false);
  const routerNavigate = useNavigate();
  testbuyItem.criteria = criteriaState;

  const { data: itemInfo, isLoading: itemInfoLoading } = useItemInfo(
    testbuyItem.shortId
  );

  const { data: testbuy, isLoading } = useTestbuy(testbuyId);
  const headerTitleText = `${itemInfo?.name} ${itemInfo?.noFormatted}`;
  const isTaskCreated = !!testbuyItem.followUpTaskId;

  const { data: testbuyFollowupTask } = useGetTestbuyFollowUpTask(
    testbuyItem?.followUpTaskId || ""
  );

  const isTaskResolved =
    testbuyFollowupTask?.state === "COMPLETED" ||
    testbuyFollowupTask?.state === "CLOSED";
  const isTaskFinishedWithinLastWeek =
    isTaskResolved && isDateWithinLastWeek(testbuyFollowupTask?.finishedAt);
  const isTaskViewable =
    !!testbuyFollowupTask &&
    isTaskCreated &&
    (!isTaskResolved || (isTaskResolved && isTaskFinishedWithinLastWeek));

  const parentLabel = formatDateISO8601(testbuy?.createdAt);

  const linkbarItems = useMemo(() => {
    return [
      {
        label: t("testBuysString"),
        to: "/testbuy",
      },
      {
        label: parentLabel || "",
        to: `/testbuy/${testbuyId}`,
      },
    ];
  }, [parentLabel, t, testbuyId]);

  return (
    <FullScreenPopup
      noPadding
      actionBarContent={
        <FixaButton
          data-testid="article-done"
          type="primary"
          text={t(isTaskCreated ? "showTaskString" : "doneString")}
          onClick={async () => {
            setLoadButton(true);
            if (allCriteriaOk(testbuyItem.criteria)) {
              navigate(`/testbuy/${testbuyId}`);
            } else if (isTaskViewable) {
              toggleModal({
                type: ModalType.TaskDetail,
                payload: {
                  taskId: testbuyItem.followUpTaskId,
                  taskType: TaskType.TESTBUY_FOLLOW_UP,
                },
                onToggle: () => {
                  routerNavigate(-1); // Navigate back when the modal is closed
                },
              });
              setLoadButton(false);
              navigate(`/testbuy/${testbuyId}/article/${testbuyItem.id}`);
            } else {
              navigate(
                `/testbuy/${testbuyId}/article/${testbuyItem.id}/createTask`
              );
            }
          }}
          disabled={
            !allCriteriaAnswered(testbuyItem.criteria) ||
            (isTaskCreated && !isTaskViewable)
          }
          loading={loadButton}
          fluid={true}
        />
      }
      linkbar={
        <Linkbar
          items={linkbarItems}
          currentLabel={headerTitleText}
          currentAsH1
          loading={!parentLabel}
        />
      }
    >
      {itemInfoLoading && <SimpleArticleCardSkeleton />}
      {!itemInfoLoading && itemInfo && (
        <div className={styles["item-card-header"]}>
          <TestbuyItemCard
            canCopyItemNo={true}
            item={itemInfo}
            homeLocationGridCode={testbuyItem.location}
            testbuyItem={testbuyItem}
          />
        </div>
      )}
      <CriteriaList
        testbuyId={testbuyId}
        articleId={testbuyItem.id || ""}
        criteria={criteriaState}
        dispatch={dispatch}
        isLocked={
          isLoading || testbuy?.status !== "IN_PROGRESS" || isTaskCreated
        }
        location={testbuyItem.location}
        price={testbuyItem.price}
      />
    </FullScreenPopup>
  );
}
